/* eslint-disable check-file/filename-naming-convention */
// src/routes/routeConfig.tsx
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, type RouteObject, useRouteError } from 'react-router-dom';

import {
  AppRoutesEnum,
  DashboardRoutesEnum,
  DesignSharedPreviewRoutesEnum,
  ProjectSharedPreviewRoutesEnum,
  StaticTextRoutesEnum,
} from '@constants/enums/Routes.enum';
import { InternalError } from '@containers/shared/InternalError';

import * as f from './factories';
import { AdminGuard, AuthGuard, NonAuthGuard, OnboardingGuard } from './guards';
import { lazyRoute } from './lazyRoute';
import { RootLayout } from './RootLayout';

/** Dashboard-scoped 404 — shows within dashboard chrome with "Go to Templates" button */
const DashboardNotFound = () => {
  const { t } = useTranslation();

  return (
    <InternalError
      errorCode="404"
      description={t('NotFound.Content')}
      isFullscreen
      buttonLabel={t('DashboardRoutes.NotFound')}
    />
  );
};

const RouteErrorBoundary = () => {
  const error = useRouteError();
  const { t } = useTranslation();

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <InternalError
      errorCode="500"
      description={t('AppInternalError.Description')}
      buttonLabel={t('AppInternalError.RefreshPage')}
      onButtonClick={() => window.location.reload()}
    />
  );
};

export const routeConfig: RouteObject[] = [
  {
    Component: RootLayout,
    ErrorBoundary: RouteErrorBoundary,
    children: [
      // ─── Public routes (always accessible) ───
      {
        path: `${ProjectSharedPreviewRoutesEnum.PROJECT_SHARE_PREVIEW}/*`,
        lazy: lazyRoute(f.projectSharedPreviewPage, 'ProjectSharedPreviewPage'),
      },
      {
        path: `${DesignSharedPreviewRoutesEnum.DESIGN_SHARE_PREVIEW}/*`,
        lazy: lazyRoute(f.designSharedPreviewPage, 'DesignSharedPreviewPage'),
      },
      {
        path: AppRoutesEnum.TEAM_INVITATION_CONFIRM_ROUTE,
        lazy: lazyRoute(
          f.teamInvitationConfirmPage,
          'TeamInvitationConfirmPage',
        ),
      },
      {
        path: AppRoutesEnum.PAYMENT_FAILED_ROUTE,
        lazy: lazyRoute(f.paymentFailedPage, 'PaymentFailedPage'),
      },
      {
        path: AppRoutesEnum.SHOPIFY_INSTALLATION_ROUTE,
        lazy: lazyRoute(f.shopifyInstallationPage, 'ShopifyInstallationPage'),
      },
      {
        path: AppRoutesEnum.MAINTENANCE_ROUTE,
        lazy: lazyRoute(f.maintenancePage, 'MaintenancePage'),
      },
      ...Object.values(StaticTextRoutesEnum).map(
        (route): RouteObject => ({
          path: route,
          lazy: lazyRoute(f.staticTextPage, 'StaticText'),
        }),
      ),

      // ─── Register (conditional on feature flag) ───
      {
        path: AppRoutesEnum.REGISTER_ROUTE,
        ...(FEATURE_MANAGER.register
          ? { lazy: lazyRoute(f.registerPage, 'RegisterPage') }
          : { element: <Navigate to={AppRoutesEnum.LOGIN_ROUTE} /> }),
      },

      // ─── Non-authenticated routes ───
      {
        element: <NonAuthGuard />,
        children: [
          {
            path: AppRoutesEnum.LOGIN_ROUTE,
            lazy: lazyRoute(f.loginPage, 'LoginPage'),
          },
          {
            path: AppRoutesEnum.RESET_PASSWORD_ROUTE,
            lazy: lazyRoute(f.resetPasswordPage, 'ResetPasswordPage'),
          },
          {
            path: AppRoutesEnum.SET_PASSWORD_ROUTE,
            lazy: lazyRoute(f.setPasswordPage, 'SetPasswordPage'),
          },
        ],
      },

      // ─── Authenticated routes ───
      {
        element: <AuthGuard />,
        children: [
          // Onboarding (outside OnboardingGuard — accessible when NOT onboarded)
          {
            path: DashboardRoutesEnum.ONBOARDING_ROUTE,
            lazy: lazyRoute(f.onboardingPage, 'OnboardingPage'),
          },

          // Onboarding guard — only passed when user.onboarding === 'FINISHED'
          {
            element: <OnboardingGuard />,
            children: [
              // ─── Dashboard layout ───
              {
                lazy: lazyRoute(f.dashboardLayout, 'Dashboard'),
                errorElement: <RouteErrorBoundary />,
                children: [
                  // Templates section (TemplatesWrapper is a pathless layout with Outlet)
                  {
                    lazy: lazyRoute(f.templatesWrapperPage, 'TemplatesWrapper'),
                    children: [
                      {
                        index: true,
                        lazy: lazyRoute(f.templatesPage, 'Templates'),
                      },
                      {
                        path: DashboardRoutesEnum.TEMPLATES_ROUTE,
                        children: [
                          {
                            index: true,
                            element: (
                              <Navigate
                                to={DashboardRoutesEnum.BASE_ROUTE}
                                replace
                              />
                            ),
                          },
                          {
                            path: ':tagId',
                            lazy: lazyRoute(
                              f.industryTemplatesPage,
                              'IndustryTemplates',
                            ),
                          },
                        ],
                      },
                    ],
                  },

                  // Dashboard pages
                  {
                    path: DashboardRoutesEnum.RECENT_ROUTE,
                    lazy: lazyRoute(f.recentPage, 'RecentPage'),
                  },
                  {
                    path: DashboardRoutesEnum.STARRED_ROUTE,
                    lazy: lazyRoute(f.starredPage, 'StarredPage'),
                  },

                  // User section (UserView keeps internal Routes)
                  {
                    path: `${DashboardRoutesEnum.USER_ROUTE}/*`,
                    lazy: lazyRoute(f.userView, 'UserView'),
                  },

                  // Admin section (guarded, AdminView keeps internal Routes)
                  {
                    element: <AdminGuard />,
                    children: [
                      {
                        path: `${DashboardRoutesEnum.ADMIN_ROUTE}/*`,
                        lazy: lazyRoute(f.adminView, 'AdminView'),
                      },
                    ],
                  },

                  // Team section
                  {
                    path: `${DashboardRoutesEnum.TEAM_ROUTE}`,
                    lazy: lazyRoute(f.teamPage, 'TeamPage'),
                    children: [
                      {
                        path: `${DashboardRoutesEnum.PROJECT_ROUTE}/*`,
                        lazy: lazyRoute(f.projectPage, 'ProjectPage'),
                      },
                      // index: renders TeamView at exactly /teams/:teamId
                      {
                        index: true,
                        lazy: lazyRoute(f.teamView, 'TeamView'),
                      },
                      // wildcard: renders TeamView for sub-paths like /teams/:teamId/brand-kit
                      {
                        path: '*',
                        lazy: lazyRoute(f.teamView, 'TeamView'),
                      },
                    ],
                  },

                  // Dashboard catch-all
                  {
                    path: '*',
                    element: <DashboardNotFound />,
                  },
                ],
              },
            ],
          },
        ],
      },

      // ─── Global catch-all ───
      {
        path: '*',
        lazy: lazyRoute(f.notFoundPage, 'NotFoundPage'),
      },
    ],
  },
];
